import React, { useEffect, useState } from 'react';
import { X, Trophy, Droplets, Dumbbell, Apple, Moon, Flame, Award, Star, Zap, Heart } from 'lucide-react';
import { triggerCelebration } from './Celebration';

// Icon map for badges
const badgeIconMap = {
  'trophy': Trophy,
  'droplets': Droplets,
  'dumbbell': Dumbbell,
  'apple': Apple,
  'moon': Moon,
  'flame': Flame,
  'award': Award,
  'star': Star,
  'zap': Zap,
  'heart': Heart,
};

const BadgeCelebrationModal = ({
  isOpen,
  onClose,
  badge,
  onCelebrationComplete
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen && badge) {
      setIsVisible(true);
      if (badge.celebration_type !== 'gif') {
        triggerCelebration(badge.celebration_type || 'confetti');
      }
    }
  }, [isOpen, badge]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      if (onCelebrationComplete) {
        onCelebrationComplete();
      }
    }, 300);
  };

  if (!isOpen || !badge) return null;

  const isGifCelebration = badge.celebration_type === 'gif' && badge.celebration_gif_url;
  const BadgeIcon = badgeIconMap[badge.badge_icon] || Trophy;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
      data-testid="badge-celebration-modal"
    >
      {/* Backdrop — soft warm charcoal so the white card pops without feeling harsh */}
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" />

      {/* Card */}
      <div
        className={`relative z-10 max-w-lg w-full mx-4 bg-white rounded-2xl border border-stone-200 shadow-2xl p-6 sm:p-8 transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button — inside the card, top-right */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
          data-testid="close-celebration-btn"
          aria-label="Close celebration"
        >
          <X size={20} />
        </button>

        {/* Badge Name */}
        <div className="text-center mb-6">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900">
            🏆 {badge.badge_name}!
          </h2>
        </div>

        {isGifCelebration ? (
          <div className="rounded-2xl overflow-hidden border border-stone-200 bg-stone-50">
            <img
              src={badge.celebration_gif_url}
              alt="Celebration"
              className="w-full h-auto max-h-[50vh] object-contain"
            />
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="relative">
              {/* Soft warm glow */}
              <div className="absolute inset-0 bg-[#EFB342]/40 rounded-full blur-3xl animate-pulse" />

              {/* Icon disc */}
              <div className="relative bg-[#EFB342] p-8 rounded-full shadow-lg border-4 border-[#B8871E]/40">
                <BadgeIcon className="w-20 h-20 md:w-28 md:h-28 text-black" strokeWidth={1.75} />
              </div>

              {/* Sparkle accents */}
              <div className="absolute -top-2 -right-2 text-3xl animate-bounce">✨</div>
              <div className="absolute -bottom-2 -left-2 text-2xl animate-bounce delay-100">⭐</div>
            </div>
          </div>
        )}

        {/* Celebration Message */}
        {badge.celebration_message && (
          <div className="text-center mt-6">
            <p className="text-stone-900 text-lg md:text-xl font-semibold leading-snug px-2">
              🎉 {badge.celebration_message}
            </p>
          </div>
        )}

        {/* Health Benefit */}
        {badge.health_benefit && (
          <div className="text-center mt-3">
            <p className="text-stone-700 text-sm md:text-base leading-relaxed px-2">
              {badge.health_benefit}
            </p>
          </div>
        )}

        {/* Tap to close hint */}
        <p className="text-center text-stone-500 text-xs sm:text-sm mt-6">
          Tap anywhere to close
        </p>
      </div>
    </div>
  );
};

export default BadgeCelebrationModal;
